// Follows entities through a migration history, so a renamed entity keeps its place in the diagram.
import type { ModelDiff, ModelSpec } from '@modelviewer/core';

export interface LineageStep {
  model: ModelSpec | null;
  /** Changes since the previous step that has a model. */
  diff: ModelDiff | null;
}

/**
 * For each step, maps entity keys to an id that stays the same for as long as the entity exists,
 * across renames. The id is the entity's last name. When a removed entity and a later one share a
 * name, the one that exists at the end keeps the plain name and the other gets `Name~2`.
 */
export function entityIds(steps: LineageStep[]): Map<string, string>[] {
  const last: string[] = [];
  const firstSeen: number[] = [];
  const perStep: Map<string, number>[] = [];
  let previous = new Map<string, number>();
  steps.forEach((step, index) => {
    if (!step.model) {
      perStep.push(previous);
      return;
    }
    const current = new Map<string, number>();
    for (const m of step.diff?.entities ?? []) {
      const lineage = previous.get(m.from);
      if (lineage !== undefined && step.model.entities[m.to]) current.set(m.to, lineage);
    }
    for (const key of Object.keys(step.model.entities)) {
      if (current.has(key)) continue;
      last.push(key);
      firstSeen.push(index);
      current.set(key, last.length - 1);
    }
    for (const [key, lineage] of current) last[lineage] = key;
    perStep.push(current);
    previous = current;
  });

  const alive = new Set(previous.values());
  const order = last.map((_, i) => i).sort((a, b) => Number(alive.has(b)) - Number(alive.has(a)) || firstSeen[b]! - firstSeen[a]! || a - b);
  const ids: string[] = [];
  const used = new Set<string>();
  for (const lineage of order) {
    let id = last[lineage]!;
    for (let n = 2; used.has(id); n++) id = `${last[lineage]}~${n}`;
    used.add(id);
    ids[lineage] = id;
  }
  return perStep.map((m) => new Map([...m].map(([key, lineage]) => [key, ids[lineage]!])));
}
